import React, { useContext, useEffect, useState } from 'react'

import { StaticContext } from 'react-router'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { Header, Grid, Button, Image, Confirm, Icon, Input, Select } from 'semantic-ui-react'
import { Box, Chip, IconButton, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableHead, TableRow, Collapse, Typography } from '@mui/material'
import { Favorite, FavoriteBorder, Cancel, ExpandCircleDown, Star, ArrowDropUp, ArrowDropDown } from '@mui/icons-material'
import GridOnIcon from '@mui/icons-material/GridOn'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import StarIcon from '@mui/icons-material/Star'

import Data, { IntegrationProvider, IProject, IClipGroup, IClip, UpdateOptions, SearchIncludingOptions } from '../../../api/Data'

import CreateStoryIcon from '../../../_images/create-story-icon-purple.png'
import VideoImagePlaceholder from '../../../_images/video-upload-placeholder.jpg'
import { clipsPerPage } from '../../../config/constants'
import { maxEpisodeDuration, maxClipsPerEpisode, minClipsPerEpisode } from '../../../config/constants'
import { GlobalContext } from '../../../contexts/GlobalContext'
import { UserContext } from '../../../contexts/UserContext'
import { downloadFileFromBrowser, flattenClipsList, reshapeClipsList } from '../../../utils/sharedFunctions'
import UploadModal from '../../upload/UploadModal'

import SubSection from '../../../components/common/SubSection'
import Title from '../../../components/common/Title'
import VideoEncodingCard from '../../../components/common/VideoEncodingCard'
import VideoErrorCard from '../../../components/common/VideoErrorCard'
import IconLink from '../../../components/icons/IconLink'

import GalleryClipCard from './CurateClipCard'
import GalleryClipGroupCard from './CurateClipGroupCard'
import GalleryClipModal from './CurateClipModal'
import GalleryControlsBar from './CurateControlsBar'
import GalleryTabNotEnoughClipSelectedModal from './CurateTabNotEnoughClipSelectedModal'

import moment from '../../../utils/Moment'
import { formatNumber } from '../../../utils/sharedFunctions'
import { CurateTableColumnConfigs } from './CurateTableColumnConfig'
import { debounce } from '../../../utils/debounce'

let autoFetchClips: any

const CurateTab = (props: IProps) => {
  const { dispatchGlobalContext } = useContext(GlobalContext)
  const { userProfile } = useContext(UserContext)

  // Props
  const { history, location, match, project, clipsSelected, setClipsSelected, exitProject } = props
  const { projectTitle } = props

  // States
  const [activeTab, setActiveTab] = useState<string>('all')

  const [clips, setClips] = useState<IClip[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  const [page, setPage] = useState<number>(1)
  const [sortColumns, setSortColumns] = useState<Record<string, 'asc' | 'desc' | undefined>>({})
  const [totalEntries, setTotalEntries] = useState<null | number>(null)
  const [tags, setTags] = useState<string[]>([])
  const [searchPhrase, setSearchPhrase] = useState<string>('')

  const [selectedClipGroup, setSelectedClipGroup] = useState<IClipGroup | null>(null)
  const [showClipModal, setShowClipModal] = useState<IClip | null>(null)
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false)
  const [showNotEnoughClipModal, setShowNotEnoughClipModal] = useState<boolean>(false)
  const [showHasTooManyClipModal, setShowHasTooManyClipModal] = useState<boolean>(false)
  const [showEpisodeDurationExceeded, setShowEpisodeDurationExceeded] = useState<number | null>(null)
  const [showBulkTagModal, setShowBulkTagModal] = useState<boolean>(false)
  const [tagName, setTagName] = useState<string>('')
  const [isTableView, setIsTableView] = useState(false)
  const [selectedClipRow, setSelectedClipRow] = useState<null | number>(null)

  const [isLoadingBulkDownloadRequest, setIsLoadingBulkDownloadRequest] = useState<boolean>(false)
  const [searchIncluding, setSearchIncluding] = useState<SearchIncludingOptions>({
    contributorDetails: false,
    tags: false,
    clipAnswers: false
  })

  // Fetch clips
  useEffect(() => {
    fetchAllUploads()

    return () => clearTimeout(autoFetchClips)
  }, [searchPhrase, tags, match.params.projectSlug, page, setClips, sortColumns, searchIncluding])

  // Check if the modal should be shown by default
  useEffect(() => {
    const shouldShowUploadModal = location.state?.uploadModal

    if (shouldShowUploadModal) {
      history.push(`/projects/${match.params.projectSlug}/curate`, { uploadModal: false })
      setShowUploadModal(true)
    }
  }, [history, location, match.params.projectSlug])

  const fetchAllUploads = async (autoFetching: boolean = false) => {
    const slug = match.params.projectSlug

    clearTimeout(autoFetchClips)

    const offset = (page - 1) * clipsPerPage
    const limit = clipsPerPage
    const orderBy = Object.entries(sortColumns)
      .filter(([_, v]) => !!v)
      .map(([k, v]) => `${k} ${v!.toUpperCase()}`)
      .join(',')

    const { data, error } = await Data.getClips({ projectSlug: slug, offset, limit, searchPhrase, tags, orderBy, searchIncluding })

    if (error) return alert('An error occurred')

    const { rows, count } = data

    setTotalEntries(count)

    const fetchedClips = flattenClipsList(rows)

    setClips(fetchedClips)

    const isClipInProgress = fetchedClips.some((clip: IClip) => clip.encodingStatus === 'encoding')

    // Clear all selected clips on reload to prevent bulk actions on clips that are no longer shown in the current search and page criteria.
    if (!autoFetching) {
      setClipsSelected([])
    }

    // If clips are encoding, fetch clips every 5 seconds
    if (isClipInProgress) autoFetchClips = setTimeout(() => fetchAllUploads(true), 5000)
  }

  // Is user collaborator
  const isUserCollaborator = (): boolean => {
    return userProfile.userAccessRights === 'collaborator'
  }

  // Add tag to search
  const addFilterTag = (value: string) => {
    let newTagsArr: string[] = []

    if (tags.includes(value)) return

    newTagsArr = [...tags, value]

    setTags(newTagsArr)

    setPage(1)
  }

  // Remove tag from search
  const removeFilterTag = (value: string) => {
    let newTagsArr: string[] = []

    if (!tags.includes(value)) return

    newTagsArr = tags.filter((tag) => tag !== value)

    setTags(newTagsArr)

    setPage(1)
  }

  // Update clip
  const updateClip = async (clip: IClip, options: UpdateOptions)=> {
    setLoading(true)
    const { data: updatedClip, error } = await Data.updateClip(clip, options.skipEncoding)

    if (error) {
      alert('An error occurred')
      return setLoading(false)
    }

    setClips((prevClips) =>
      prevClips.map((c) => (c.id === updatedClip.id ? updatedClip : c))
    )

    // Trick to update the clip and show it in progress
    if (options.reloadPage) {
      return setTimeout(() => {
        setLoading(false)
        setShowClipModal(null)
        window.location.reload()
      }, 2000)
    } else {
      setLoading(false)
    }
  }

  // Delete clip
  const deleteClip = async (clipId: number) => {
    const { error } = await Data.deleteClip(clipId)

    if (error) return

    const newClips = clips.filter((item) => item.id !== clipId)

    setClips(newClips)

    setShowClipModal(null)
  }

  // Set clip as favorite
  const handleFavorite = async (clipId: number) => {
    const { data, error } = await Data.setClipAsFavorite(clipId)

    if (error) return

    const updatedClip: IClip = data

    const newClips1: IClip[] = clips.reduce((acc: IClip[], el: IClip) => {
      if (el.id === clipId) {
        acc.push(updatedClip)
      }
      else {
        acc.push(el)
      }
      
      return acc
    }, [])

    setClips(newClips1)
  }

  // handle clip selection
  const handleClipSelection = (clip: IClip) => {
    const clipIndex = clipsSelected.findIndex((clipSelected: IClip) => clipSelected.id === clip.id)

    if (clipIndex === -1) {
      // Add clip to clipSelected array
      return setClipsSelected([...clipsSelected, clip])
    }

    // Remove clip from clipSelected array
    return setClipsSelected(clipsSelected.filter((clipSelected, index) => index !== clipIndex))
  }

  // handle clip group selection
  const handleClipGroupSelection = (clipGroup: IClipGroup | null) => {
    setSelectedClipGroup(clipGroup)
  }

  // Download report
  const downloadCsvReport = async () => {
    const { data, error } = await Data.getCsvReport(project.slug)

    if (error) {
      if (error.response.status === 403) {
        return dispatchGlobalContext({ type: 'show update plan modal' })
      }
      return alert('An error occurred')
    }

    const { reportData, reportFileName } = data

    return downloadFileFromBrowser({ data: reportData, type: 'text/csv', fileName: reportFileName })
  }

  // Bulk download clips to zip
  const bulkDownloadClipsToZip = async () => {
    setIsLoadingBulkDownloadRequest(true)
    const clipIds = clipsSelected.map((clip: { id: number }) => clip.id)
    const projectSlug = match.params.projectSlug

    try {
      await Data.bulkDownloadClips(projectSlug, clipIds)
      setClipsSelected([])
      setIsLoadingBulkDownloadRequest(false)
      alert(
        `We're preparing your bulk download. Bulk download can take between 1 to 15 minutes to complete. Go to your projects "Exports" section to see your projects available bulk downloads.`
      )
    } catch (error) {
      alert('An error occurred')
      setIsLoadingBulkDownloadRequest(false)
    }
  }

  // Bulk export clips to Google Drive
  const bulkExportClipsToGoogleDrive = async () => {
    setClipsSelected([])

    // alert(`Sorry, we are unable to complete your bulk export to Google Drive at this time.`)

    setIsLoadingBulkDownloadRequest(true)
    const clipIds = clipsSelected.map((clip: { id: number }) => clip.id)
    const projectSlug = match.params.projectSlug

    try {
      await Data.bulkExportClips(projectSlug, clipIds, IntegrationProvider.GOOGLE)
      setClipsSelected([])
      setIsLoadingBulkDownloadRequest(false)
      alert(
        `We're preparing your bulk export. Bulk exports to Google Drive can take between 1 to 15 minutes to complete. Go to your projects "Exports" section to see your export history and progress.`
      )
    } catch (error) {
      alert('An error occurred')
      setIsLoadingBulkDownloadRequest(false)
    }
  }

  // Bulk export clips to Microsoft OneDrive
  const bulkExportClipsToMicrosoftOneDrive = async () => {
    setClipsSelected([])

    alert(`Sorry, we are unable to complete your bulk export to Microsoft OneDrive at this time.`)

    // setIsLoadingBulkDownloadRequest(true)
    // setIsLoadingBulkDownloadRequest(true)
    // const clipIds = clipsSelected.map((clip: { id: number }) => clip.id)
    // const projectSlug = match.params.projectSlug

    // try {
    //   await Data.bulkExportClips(projectSlug, clipIds, IntegrationProvider.MICROSOFT)
    //   setClipsSelected([])
    //   setIsLoadingBulkDownloadRequest(false)
    //   alert(
    //     `We're preparing your bulk export. Bulk exports to Microsoft OneDrive can take between 1 to 15 minutes to complete. Go to your projects "Exports" section to see your export history and progress.`
    //   )
    // } catch (error) {
    //   alert('An error occurred')
    //   setIsLoadingBulkDownloadRequest(false)
    // }
  }

  const handleVote = async (clip: IClip | null, vote: boolean): Promise<void> => {
    // Check if clip is null and return or handle accordingly
    if (!clip) {
      console.log("Clip is null, cannot proceed with voting.")
      return // Or handle as needed
    }
  
    const { data, error } = await Data.setClipVote(clip.id, vote)
    if (error) return alert('An error occurred')
  
    const updatedClip: IClip = data

    const newClips: IClip[] = clips.reduce((acc: IClip[], el: IClip) => {
      if (el.id === clip.id) {
        acc.push(updatedClip)
      }
      else {
        acc.push(el)
      }

      return acc
    }, [])

    setClips(newClips)
  }

  const handleKeyPressOnBulkTagInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleBulkUpdateClipsWithTag()
    }
  }

  const handleShowBulkTagModal = () => {
    if (clipsSelected.length === 0) {
      return alert('Please select at least one clip to bulk tag')
    }
    setShowBulkTagModal(true)
  }

  const handleBulkUpdateClipsWithTag = async () => {
    setShowBulkTagModal(false)
    const clipIds = clipsSelected.map((clip: { id: number }) => clip.id)
    try {
      await Data.bulkUpdateClipsWithTag(tagName, clipIds)
      fetchAllUploads()
      setClipsSelected([])
    } catch (error) {
      alert('An error occurred')
    }
  }

  const handleSortColumnClick = (propertyName: string, isCumulative: boolean) => {
    const currentColumnState = sortColumns[propertyName]
    const newState = {
      ...isCumulative ? sortColumns : {},
      [propertyName]: currentColumnState === 'asc' ? 'desc' :
        currentColumnState === 'desc' ? undefined : 'asc',
    } as Record<string, 'asc' | 'desc'>

    setSortColumns(newState)
  }

  // Create story
  const handleCreateVideoStory = () => {
    const hasNotEnoughClipSelected = !clipsSelected || clipsSelected.length < minClipsPerEpisode
    if (hasNotEnoughClipSelected) return setShowNotEnoughClipModal(true)

    const hasTooManyClipSelected = clipsSelected.length > maxClipsPerEpisode
    if (hasTooManyClipSelected) return setShowHasTooManyClipModal(true)

    const episodeDuration = Math.floor(
      clipsSelected.reduce((acc, clip) => (clip.duration ? acc + clip.duration : acc), 0)
    )
    if (episodeDuration > maxEpisodeDuration) return setShowEpisodeDurationExceeded(episodeDuration)

    return history.push(`/projects/${match.params.projectSlug}/combine`)
  }

  const filterByFavorite = () => {
    setActiveTab((prevState) => (prevState === 'all' ? 'favorites' : 'all'))
  }

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = (seconds % 60).toFixed(2)
  
    if (minutes > 0) {
      return `${minutes} min ${remainingSeconds} sec`
    }
    else {
      return `${remainingSeconds} sec`
    }
  }

  const renderRating = (rating: number) => {
    if (rating !== null) {
      return (
        <Box>
          {Array.from({ length: rating }, (_, i) => (
            <StarIcon key={i} style={{ color: '#6b50ff' }} />
          ))}
        </Box>
      )
    }

    return (
      <em>not specified</em>
    )
  }

  const calculateClipGroupDuration = (clipGroup: IClipGroup) => {
    return clipGroup.clips.reduce((total: number, clip: IClip) => {
      return total += clip.duration
    }, 0)
  }

  const calculateClipGroupVotes = (clipGroup: IClipGroup) => {
    return clipGroup.clips.reduce((total: number, clip: IClip) => {
      return total += clip.votes.length
    }, 0)
  }

  if (!clips || totalEntries === null) return null

  let clipsFiltered = activeTab === 'favorites' ? clips.filter((clip) => clip.isFavorite) : clips

  if (selectedClipGroup !== null) {
    clipsFiltered = clipsFiltered.filter((clip) => clip.submissionId === selectedClipGroup.submissionId)
  }

  const { plan } = userProfile!

  const { maxNbOfClips } = plan

  console.log('Rendering Clip Gallery Page', clipsFiltered)

  return (
    <>
      <Title title={projectTitle}>
        <>
          <ToggleButtonGroup
            size="small"
            value={isTableView ? 'list' : 'grid'}
            exclusive
            aria-label="view mode"
            onChange={(event, newViewMode) => {
              if (newViewMode !== null) {
                setIsTableView(newViewMode === 'list')
              }
            }}
            sx={{
              marginRight: '1rem',
              color: 'white'
            }}
          >
            <ToggleButton 
              value="grid" 
              aria-label="grid view" 
              sx={{
                color: 'white',
                borderColor: 'white',
                backgroundColor: !isTableView ? 'grey' : 'transparent',
                gap: '5px',
                '&.Mui-selected': {
                  color: 'white',
                  borderColor: 'white',
                  backgroundColor: 'grey'
                },
                '&:hover': {
                  backgroundColor: !isTableView ? 'grey' : 'transparent'
                }
              }}
            >
              <GridOnIcon/>
              Grid
            </ToggleButton>
            <ToggleButton 
              value="list" 
              aria-label="list view" 
              sx={{
                color: 'white',
                borderColor: 'white',
                backgroundColor: isTableView ? 'grey' : 'transparent',
                gap: '5px',
                '&.Mui-selected': {
                  color: 'white',
                  borderColor: 'white',
                  backgroundColor: 'grey'
                },
                '&:hover': {
                  backgroundColor: isTableView ? 'grey' : 'transparent'
                }
              }}
            >
              <FormatListBulletedIcon />
              List
            </ToggleButton>
          </ToggleButtonGroup>
          {!isUserCollaborator() && (
            <>
              <Button compact secondary onClick={downloadCsvReport} style={{ border: '1px solid #ffffff', whiteSpace: 'nowrap' }}>
                <Icon name="file excel"></Icon>
                Project Report
              </Button>
              <Select
                loading={isLoadingBulkDownloadRequest}
                placeholder="Bulk actions"
                disabled={clipsSelected.length <= 0}
                value={0}
                options={[
                  {
                    key: 'bulkDownloadToZip',
                    icon: 'zip',
                    text: 'Download archive',
                    value: 'bulkDownloadToZip',
                  },
                  {
                    key: 'bulkExportToGoogleDrive',
                    icon: 'google drive',
                    text: 'To Google Drive',
                    value: 'bulkExportToGoogleDrive',
                  },
                  {
                    key: 'bulkExportToMicrosoftOneDrive',
                    icon: 'microsoft',
                    text: 'To OneDrive',
                    value: 'bulkExportToMicrosoftOneDrive',
                  },
                ]}
                onChange={(e, { value }) => {
                  if (value === 'bulkDownloadToZip') {
                    return bulkDownloadClipsToZip()
                  }
                  else if (value === 'bulkExportToGoogleDrive') {
                    return bulkExportClipsToGoogleDrive()
                  }
                  else if (value === 'bulkExportToMicrosoftOneDrive') {
                    return bulkExportClipsToMicrosoftOneDrive()
                  }
                }}
                style={{ marginRight: '1rem' }}
              />
            </>
          )}
          <Button
            compact
            color="orange"
            onKeyDown={() => setShowUploadModal(true)}
            onClick={() => setShowUploadModal(true)}
            style={{ whiteSpace: 'nowrap' }}
          >
            <Icon name="upload"></Icon>
            Upload Clip
          </Button>
          <Button compact onClick={exitProject}>
            Close
          </Button>
        </>
      </Title>

      <SubSection hasGreyBackground>
        <>
          <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
            Project Video Library
            <Header.Subheader>
              Organize, categorize, curate and tag every video clip in your dynamic video submission library. Transform raw submissions into a curated video archive that tells your organisation’s story.
            </Header.Subheader>
          </Header>
        
          <GalleryControlsBar
            totalTeamsClipCount={project.totalNumberOfTeamClips}
            projectEntryCount={totalEntries}
            projectClipCount={project.clipCount}
            clipMax={project.clipMax}
            tags={tags}
            removeFilterTag={removeFilterTag}
            setPage={setPage}
            page={page}
            clipsPerPage={clipsPerPage}
            showPagination={selectedClipGroup === null}
            maxNbOfClips={maxNbOfClips}
            activeTab={activeTab}
            filterByFavorite={filterByFavorite}
            onBulkTag={handleShowBulkTagModal}
            onSearch={debounce(setSearchPhrase, 500)}
            onSelectAll={() => {
              if (clipsSelected.length > 0) {
                return setClipsSelected([])
              }

              // Only select clips that are encoded
              const encodedClips = clipsFiltered.filter((clip) => clip.encodingStatus === 'encoded')

              return setClipsSelected(encodedClips)
            }}
            onBack={() => handleClipGroupSelection(null)}
            clipsSelected={clipsSelected}
            searchIncluding={searchIncluding}
            onSearchIncludingChange={(newOptions) => setSearchIncluding(newOptions)}
          />
          {isTableView ? (
            <>
              <Button
                compact
                primary
                onClick={handleCreateVideoStory}
                style={{
                  margin: '20px 0',
                  whiteSpace: 'nowrap'
                }}
              >
                Create a Story
              </Button>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#13165a' }}>
                    {
                      CurateTableColumnConfigs.map(col =>
                        <TableCell
                          key={col.id}
                          align={col.titleAlignment}
                          sx={{ fontWeight: 'bold', color: '#ffffff', cursor: col.isSortable ? 'pointer' : 'default', }}
                          sortDirection={col.isSortable ?
                            sortColumns[col.sortByPropertyName] || false :
                            false
                          }
                          onClick={col.isSortable ?
                            (e) => handleSortColumnClick(col.sortByPropertyName, e.ctrlKey) :
                            undefined}
                        >
                          {col.title}
                          {col.isSortable && sortColumns[col.sortByPropertyName] &&
                            (
                              sortColumns[col.sortByPropertyName] === 'asc' ?
                                <ArrowDropUp key={`${col.id}-up`} fontSize="small" style={{ verticalAlign: 'text-bottom' }} /> :
                                <ArrowDropDown key={`${col.id}-down`} fontSize="small" style={{ verticalAlign: 'text-bottom' }} />
                            )
                          }
                        </TableCell>
                      )
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clipsFiltered?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Box my={1}>
                          No clips uploaded
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (selectedClipGroup === null ? reshapeClipsList(clipsFiltered) : clipsFiltered).map((submission, i) => (
                      'id' in submission ? (
                        <>
                          <TableRow
                            key={`${i}a`}
                            onClick={!submission.isRestricted ? () => setShowClipModal(submission) : undefined}
                            sx={!submission.isRestricted ? {
                              cursor: 'pointer',
                              transition: 'background-color 0.3s',
                              '&:hover': {
                                backgroundColor: '#f0f0f0'
                              }
                            } : {
                              opacity: 0.4
                            }}
                          >
                            <TableCell rowSpan={2} align="center">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleClipSelection(submission)
                                }}
                              >
                                {clipsSelected.map((el) => el.id).includes(submission.id) ? (
                                  <CheckCircleIcon sx={{ color: '#6b50ff' }} />
                                ) : (
                                  <CheckCircleOutlineIcon sx={{ color: '#bbbbbb' }} />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell rowSpan={2} align="center">
                              <img src={submission.imageLowResUrl} style={{ height: '50px', aspectRatio: '16/9', objectFit: 'cover', borderRadius: '4px' }} />
                            </TableCell>
                            <TableCell>
                              {submission.contributor || 'unknown'}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedClipRow(selectedClipRow !== submission.id ? submission.id : null)
                                }}
                              >
                                <ExpandCircleDown />
                              </IconButton>
                            </TableCell>
                            <TableCell align="right">
                              {moment(submission.createdAt).format('ll')}
                            </TableCell>
                            <TableCell align="right">
                              {formatDuration(submission.duration)}
                            </TableCell>
                            <TableCell align="right">
                              {formatNumber(submission.votes.length)}
                            </TableCell>
                            <TableCell align="center">
                              {submission.status === 200 ? (
                                <Star sx={{ color: 'gold' }} />
                              ) : (
                                <Cancel sx={{ color: 'black' }} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                disabled={!!submission.isRestricted}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleFavorite(submission.id)
                                }}
                                sx={{ color: submission.isFavorite ? '#ff0000' : 'inherit' }}
                              >
                                {submission.isFavorite ? (
                                  <Favorite />
                                ) : (
                                  <FavoriteBorder />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell />
                            <TableCell />
                          </TableRow>
                          {((submission.question || '').trim() !== '' || submission.tags.length > 0) && (
                            <TableRow
                              key={`${i}b`}
                              onClick={!submission.isRestricted ? () => setShowClipModal(submission) : undefined}
                              sx={!submission.isRestricted ? {
                                cursor: 'pointer',
                                transition: 'background-color 0.3s',
                                '&:hover': {
                                  backgroundColor: '#f0f0f0'
                                }
                              } : {
                                opacity: 0.4
                              }}
                            >
                              <TableCell colSpan={9}>
                                {(submission.question || '').trim() !== '' && (
                                  <p><strong>Question: </strong>{submission.question}</p>
                                )}
                                {submission.tags.length > 0 && (
                                  <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                                    <strong>Tags:</strong>&nbsp;
                                    {submission.tags.map((tag) => (
                                      <Chip
                                        key={tag}
                                        label={tag}
                                        variant="outlined"
                                        disabled={!!submission.isRestricted}
                                        onClick={(e) => {
                                          e.stopPropagation()
                                          addFilterTag(tag)
                                        }}
                                        sx={{
                                          color: '#6b50ff',
                                          borderColor: '#6b50ff',
                                          height: '24px',
                                          borderRadius: '4px',
                                          padding: '0 8px',
                                          fontSize: '0.75rem'
                                        }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          <TableRow key={`${i}c`}>
                            <TableCell colSpan={11} sx={{ padding: 0 }}>
                              <Collapse in={selectedClipRow === submission.id} timeout="auto" unmountOnExit>
                                <Table sx={{ backgroundColor: '#ffffff' }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>
                                        <Typography variant="h6">Contributor Details</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="h6">Data Collection</Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="h6">Clip</Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                    <TableBody>
                                      <TableRow>
                                        <TableCell>
                                          <strong>Name:</strong> {submission.contributor}
                                        </TableCell>
                                        <TableCell>
                                          <strong>Consent Granted:</strong> {submission.contentOwnership ? 'Yes' : 'No'}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell>
                                          <strong>Location:</strong> {submission.location || <em>no location</em>}
                                        </TableCell>
                                        <TableCell>
                                          <strong>Timestamp:</strong> {moment(submission.createdAt).format('ll')}
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell />
                                        <TableCell>
                                          <strong>Comments:</strong>
                                        </TableCell>
                                      </TableRow>
                                      <TableRow>
                                        <TableCell />
                                        <TableCell>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <strong>Rating:</strong> {renderRating(submission.rating)}
                                          </Box>
                                        </TableCell>
                                        <TableCell />
                                      </TableRow>
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow
                          key={i}
                          onClick={() => handleClipGroupSelection(submission)}
                          sx={{
                            backgroundColor: '#e1e2fa',
                            cursor: 'pointer',
                            transition: 'background-color 0.3s',
                            '&:hover': {
                              backgroundColor: '#d0d1f7'
                            }
                          }}
                        >
                          <TableCell align="center" />
                          <TableCell align="center">
                            <img src={submission.clips[0].imageLowResUrl} style={{ height: '50px', aspectRatio: '16/9', objectFit: 'cover', borderRadius: '4px' }} />
                          </TableCell>
                          <TableCell>
                            {submission.clips[0].contributor || 'unknown'}
                          </TableCell>
                          <TableCell align="center" />
                          <TableCell align="right">
                            {moment(submission.clips[0].createdAt).format('ll')}
                          </TableCell>
                          <TableCell align="right">
                            {formatDuration(calculateClipGroupDuration(submission))}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(calculateClipGroupVotes(submission))}
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell align="right">
                            <strong>{formatNumber(submission.clips.length)}</strong>
                          </TableCell>
                          <TableCell align="center">
                            <IconLink />
                          </TableCell>
                        </TableRow>
                      )
                    ))
                  )}
                </TableBody>
              </Table>
            </>
          ) : (
            <Grid columns={4} doubling stackable>
              {/* clips gallery */}
              {!isUserCollaborator() && (
                <Grid.Column textAlign="center">
                  <div
                    style={{ position: 'relative', cursor: 'pointer' }}
                    tabIndex={-1}
                    role="button"
                    onClick={handleCreateVideoStory}
                  >
                    <Image src={VideoImagePlaceholder} rounded style={{ border: '1px solid #6b50ff' }} />
                    <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '100%' }}>
                      <Image src={CreateStoryIcon} style={{ margin: '1.5rem auto -1rem auto', width: '4rem' }} />
                      <Header as="h4" color="violet">
                        CREATE A STORY
                      </Header>
                    </div>
                  </div>
                </Grid.Column>
              )}
              {clipsFiltered?.length > 0 && (
                (selectedClipGroup === null ? reshapeClipsList(clipsFiltered) : clipsFiltered).map((submission, i) => (
                  'id' in submission ? (
                    <Grid.Column key={i}>
                      {submission.encodingStatus === 'encoded' && (
                        <GalleryClipCard
                          userId={userProfile.id}
                          handleClipSelection={handleClipSelection}
                          clip={submission}
                          clipsSelected={clipsSelected}
                          addFilterTag={addFilterTag}
                          setShowClipModal={setShowClipModal}
                          handleFavorite={handleFavorite}
                          votes={submission.votes}
                          handleVote={handleVote}
                        />
                      )}
                      {submission.encodingStatus === 'encoding' && (
                        /* If not encoded yet, display encoding message */
                        <VideoEncodingCard status={submission.status} />

                        // <GalleryClipInProgressCard isMobile={isMobile} clip={submission} />
                      )}
                      {submission.encodingStatus === 'error' && (
                        /* If encoding error, display error message */
                        <VideoErrorCard videoId={submission.id} handleDelete={deleteClip} />
                      )}
                    </Grid.Column>
                  ) : (
                    <Grid.Column key={i}>
                      <GalleryClipGroupCard
                        clipGroup={submission}
                        handleClipGroupSelection={handleClipGroupSelection}
                      />
                    </Grid.Column>
                  )
                ))
              )}
            </Grid>
          )}
          {/* Bulk tag modal */}
          <Confirm
            open={showBulkTagModal}
            header="Input a tag name to bulk tag the selected clips"
            confirmButton="Save"
            content={
              <Input
                compact
                placeholder="Enter a tag"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                onKeyPress={handleKeyPressOnBulkTagInput}
                style={{ width: '100%' }}
              />
            }
            cancelButton="Cancel"
            onCancel={() => setShowBulkTagModal(false)}
            onConfirm={handleBulkUpdateClipsWithTag}
          />
          {/* Project upload modal */}
          {showUploadModal && (
            <UploadModal project={project} setShowModal={setShowUploadModal} />
          )}
          {/* Clip edit modal */}
          {showClipModal && (
            <GalleryClipModal
              loading={loading}
              clip={showClipModal}
              clipGroup={selectedClipGroup}
              setShowModal={setShowClipModal}
              updateClip={updateClip}
              deleteClip={deleteClip}
              displaySetting={project.displaySetting}
            />
          )}
          {/* Not enough clip modal alert */}
          {showNotEnoughClipModal && (
            <GalleryTabNotEnoughClipSelectedModal setShowModal={setShowNotEnoughClipModal} />
          )}
          {/* Too many clip modal alert */}
          <Confirm
            open={showHasTooManyClipModal}
            header="Too many clips selected"
            content={`Video stories can be made with ${minClipsPerEpisode} to ${maxClipsPerEpisode} clips maximum. Unselect some clips to create your video story.`}
            confirmButton="OK"
            cancelButton={null}
            onConfirm={() => setShowHasTooManyClipModal(false)}
          />
          {/* Episode duration too long */}
          <Confirm
            open={!!showEpisodeDurationExceeded}
            header="Episode duration exceeded"
            content={`Video story cannot exceed ${maxEpisodeDuration}s (currently ${showEpisodeDurationExceeded}s). Unselect some clips to create your video story.`}
            confirmButton="OK"
            cancelButton={null}
            onConfirm={() => setShowEpisodeDurationExceeded(null)}
          />
        </>
      </SubSection>
    </>
  )
}

type LocationState = {
  uploadModal: boolean
}

interface IProps extends RouteComponentProps<any, StaticContext, LocationState> {
  projectTitle: string
  project: IProject
  clipsSelected: IClip[]
  setClipsSelected: (arg: IClip[]) => void
  exitProject: () => void
}

export default withRouter(CurateTab)
